import React, { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { AccountContext } from "../store/AccountContext";
import { OverviewContext } from "../store/OverviewContext";
import DefaultLayout from "./DefaultLayout";
import { Box } from "@mui/material";
import BackdropLoader from "../components/common/BackdropLoader";
import UpdateEmailBanner from "../components/Banners/UpdateEmail";
import { storeReturnPath } from "../utils/returnPath";

/**
 * PM5 T1 (chrome, batch "layouts") — converted from MUI v4 + `makeStyles` to
 * MUI v5+ (`@mui/material`) `sx`. See docs/mui-conversion-guide.md.
 *
 * The v4 `useStyles` also had a `loading` key that no element in this file ever
 * referenced — dropped rather than carried forward as an unused `sx` constant.
 */

// was: useStyles().background, applied via `className` to two Boxes.
// `paddingBottom: 4` is the sx spelling of `theme.spacing(4)` (32px — the
// spacing unit is 8 on both themes), and `background.dark` is the same palette
// path the v4 rule read off `theme.palette`.
const backgroundSx = {
  width: "100%",
  height: "100%",
  flexGrow: 1,
  backgroundColor: "background.dark",
  paddingBottom: 4,
};

// #1072: with Free Feed on for them (`freeTierOpen`, from /profile/me), a
// lapsed member who opens an episode from a link sees the episode page
// instead of being sent to their account. Whether it plays in full or as a
// trailer stays the API's answer (Video.tsx loadVideo).
//
// #1242: `standard-incomplete` is let through too. Only these four statuses;
// `locked` is always redirected.
const FREE_TIER_LAPSED_STATUSES = [
  "standard-expired",
  "trial-expired",
  "new-trial-eligible",
  "standard-incomplete",
];

// The pages an episode link opens: link.farmflix.tv/video/<id> reaches the
// website as /link/video/<id>, which LinkResolver sends to
// /video-details/<id> (utils/linkRoutes.ts). Video.tsx sends a visitor it
// doesn't yet see as signed in on to /preview/<id>-preview. The same episode
// page at /videos/<tag>/<slug> is reached from inside the site, not from a
// link, so it keeps today's redirect.
const FREE_TIER_EPISODE_LINK_PATHS = ["/video-details/", "/preview/"];

// Missing or anything but `true` counts as off, so the gate fails closed.
// The router matches routes without regard to case, so the prefix check does too.
const isFreeTierEpisodeLink = (userProfile, pathname) => {
  const path = pathname.toLowerCase();
  return (
    userProfile?.freeTierOpen === true &&
    FREE_TIER_LAPSED_STATUSES.includes(userProfile.memberStatus) &&
    FREE_TIER_EPISODE_LINK_PATHS.some((prefix) => path.startsWith(prefix))
  );
};

const LoggedInLayout = ({ children }) => {
  const { loading, loggedIn, userProfile } = useContext(AccountContext);
  const { overview } = useContext(OverviewContext);
  const navigate = useNavigate();
  let location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirecting is a side effect, so it must not run in the render body:
  // StrictMode's dev double-render (and React 18's double-fired mount
  // effects) would push /login twice. The ref survives StrictMode's
  // effect re-run, keeping the push once-only (#637).
  const shouldRedirectToLogin = !loading && !loggedIn;
  const redirectedToLogin = useRef(false);
  useEffect(() => {
    if (shouldRedirectToLogin && !redirectedToLogin.current) {
      redirectedToLogin.current = true;
      // Remember where the visitor was heading so the sign-in flow can
      // return them there instead of the /videos default (#571).
      storeReturnPath(location.pathname + location.search);
      navigate("/login");
    }
  }, [shouldRedirectToLogin, navigate, location.pathname, location.search]);

  if (shouldRedirectToLogin) {
    return null;
  }

  // Loading Screen - May still this up slightly
  if (loading && !loggedIn) {
    return (
      <DefaultLayout showTry={false} showSignIn={false}>
        <Box sx={backgroundSx} />
        <BackdropLoader />
      </DefaultLayout>
    );
  }

  // Member has a Expired Subscription (paid at least once) - lets welcome them back - get them back into the clan

  // A Free Feed episode link skips the three lapsed-member redirects below
  // and renders the page (#1072).
  const freeTierEpisodeLink =
    loggedIn && isFreeTierEpisodeLink(userProfile, location.pathname);

  if (
    loggedIn &&
    !freeTierEpisodeLink &&
    (userProfile.memberStatus === "standard-expired" ||
      userProfile.memberStatus === "trial-expired" ||
      userProfile.memberStatus === "standard-incomplete" ||
      userProfile.memberStatus === "locked" ||
      userProfile.memberStatus === "new-trial-eligible") &&
    overview &&
    !overview.doors.open &&
    !overview.doors.freeDailyContentOpen
  ) {
    return <Navigate to="/myaccount/overview" replace />;
  }

  if (
    loggedIn &&
    !freeTierEpisodeLink &&
    (userProfile.memberStatus === "standard-expired" ||
      userProfile.memberStatus === "trial-expired" ||
      userProfile.memberStatus === "standard-incomplete" ||
      userProfile.memberStatus === "locked" ||
      userProfile.memberStatus === "new-trial-eligible") &&
    overview &&
    overview.doors.open &&
    !overview.doors.freeDailyContentOpen
  ) {
    return <Navigate to="/myaccount/overview" replace />;
  }

  if (
    loggedIn &&
    !freeTierEpisodeLink &&
    (userProfile.memberStatus === "standard-expired" ||
      userProfile.memberStatus === "trial-expired" ||
      userProfile.memberStatus === "standard-incomplete" ||
      userProfile.memberStatus === "locked" ||
      userProfile.memberStatus === "new-trial-eligible") &&
    overview &&
    overview.doors.open &&
    overview.doors.freeDailyContentOpen
  ) {
    return <Navigate to="/welcomeback" replace />;
  }

  // Trial Member - Trial Expired - Redirect To Subscription Window
  // if (loggedIn && !loading && userProfile.memberStatus === "trial-expired") {
  //   return <Navigate to="/myaccount/overview" replace />;
  //   // return <Redirect to="/welcomebacktrial" />;
  // }

  // Member is Locked
  // Open Period = /membership-unlock || Closed Period = /membership-lock
  // if (loggedIn && !loading && userProfile.memberStatus === "locked") {
  //   return <Navigate to="/myaccount/overview" replace />;
  // }

  // if (
  //   loggedIn &&
  //   !loading &&
  //   userProfile.memberStatus === "standard-incomplete"
  // ) {
  //   return <Navigate to="/myaccount/overview" replace />;
  // }

  // Member is Trial
  if (loggedIn && userProfile.memberStatus === "trial") {
    return (
      <DefaultLayout>
        <Box sx={backgroundSx}>{children}</Box>
      </DefaultLayout>
    );
  }
  const displayEmailWarning =
    location.pathname === "/videos" && userProfile.emailRejected;

  return (
    <DefaultLayout>
      <Box sx={backgroundSx}>
        {displayEmailWarning && <UpdateEmailBanner />}
        {children}
      </Box>
    </DefaultLayout>
  );
};

export default LoggedInLayout;
